'use client'

import { useState, useRef, forwardRef } from 'react'
import {
  Upload,
  X,
  Film,
  CheckCircle,
  Link,
  Youtube,
  Video,
  Globe,
  AlertCircle,
  Loader2,
  PlayCircle
} from 'lucide-react'
import { cn } from '../utils/styles'

export interface VideoUploadData {
  title: string
  description?: string
  tags?: string
  duration?: number
}

export interface VideoUploadProps {
  onFileUpload?: (file: File, data: VideoUploadData) => void | Promise<void>
  onUrlUpload?: (url: string, data: VideoUploadData) => void | Promise<void>
  onCancel?: () => void
  onProgress?: (progress: number) => void
  loading?: boolean
  uploadProgress?: number
  disabled?: boolean
  maxFileSize?: number
  acceptedTypes?: string[]
  supportedPlatforms?: string[]
  className?: string

  // Text customization
  uploadTabText?: string
  urlTabText?: string
  titleLabel?: string
  titlePlaceholder?: string
  descriptionLabel?: string
  descriptionPlaceholder?: string
  tagsLabel?: string
  tagsPlaceholder?: string
  tagsHelpText?: string
  uploadButtonText?: string
  uploadingText?: string
  cancelButtonText?: string
  completeText?: string

  // Upload area
  clickToUploadText?: string
  dragDropText?: string
  fileTypesText?: string
  dropHereText?: string

  // URL section
  urlLabel?: string
  urlPlaceholder?: string
  urlHelpText?: string
  detectedText?: string

  // Validation
  titleRequiredText?: string
  fileTooLargeText?: string
  invalidFileTypeText?: string
  invalidUrlText?: string

  // Visual customization
  showTabs?: boolean
  showThumbnail?: boolean
  showProgress?: boolean
  showFileInfo?: boolean
  allowUrls?: boolean
  allowTags?: boolean
  requireTitle?: boolean
  dropzoneHeight?: string
}

// Video platform detection utilities
const validateVideoUrl = (url: string) => {
  if (!url) return { valid: false, error: 'URL is required' }

  try {
    const urlObj = new URL(url)

    // YouTube
    if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
      return { valid: true, platform: 'youtube' }
    }

    // Vimeo
    if (urlObj.hostname.includes('vimeo.com')) {
      return { valid: true, platform: 'vimeo' }
    }

    // Dailymotion
    if (urlObj.hostname.includes('dailymotion.com') || urlObj.hostname.includes('dai.ly')) {
      return { valid: true, platform: 'dailymotion' }
    }

    // Generic video URLs
    if (url.match(/\.(mp4|webm|ogg|mov|avi|mkv)(\?|$)/i)) {
      return { valid: true, platform: 'direct' }
    }

    return { valid: false, error: 'Unsupported video platform' }
  } catch {
    return { valid: false, error: 'Invalid URL format' }
  }
}

const generateThumbnail = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      reject(new Error('Canvas not supported'))
      return
    }

    video.onloadedmetadata = () => {
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      video.currentTime = Math.min(1, video.duration / 4) // Get frame at 1s or 1/4 duration
    }

    video.onseeked = () => {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      resolve(canvas.toDataURL('image/jpeg', 0.8))
      URL.revokeObjectURL(video.src)
    }

    video.onerror = () => {
      reject(new Error('Failed to load video'))
    }

    video.src = URL.createObjectURL(file)
    video.load()
  })
}

const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')

    video.onloadedmetadata = () => {
      resolve(video.duration)
      URL.revokeObjectURL(video.src)
    }

    video.onerror = () => {
      reject(new Error('Failed to get video duration'))
    }

    video.src = URL.createObjectURL(file)
    video.load()
  })
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export const VideoUpload = forwardRef<HTMLDivElement, VideoUploadProps>(({
  onFileUpload,
  onUrlUpload,
  onCancel,
  onProgress,
  loading = false,
  uploadProgress = 0,
  disabled = false,
  maxFileSize = 500 * 1024 * 1024, // 500MB
  acceptedTypes = ['video/*'],
  supportedPlatforms = ['YouTube', 'Vimeo', 'Dailymotion'],
  className,

  // Text props
  uploadTabText = "Upload File",
  urlTabText = "Add URL",
  titleLabel = "Title",
  titlePlaceholder = "Enter video title",
  descriptionLabel = "Description",
  descriptionPlaceholder = "Add a description (optional)",
  tagsLabel = "Tags",
  tagsPlaceholder = "Enter tags separated by commas (optional)",
  tagsHelpText = "Example: rehearsal, team-a, practice",
  uploadButtonText = "Upload Video",
  uploadingText = "Uploading...",
  cancelButtonText = "Cancel",
  completeText = "Upload complete",

  clickToUploadText = "Click to upload",
  dragDropText = "or drag and drop",
  fileTypesText = "MP4, MOV, AVI, MKV or WebM",
  dropHereText = "Drop your video here",

  urlLabel = "Video URL",
  urlPlaceholder = "https://www.youtube.com/watch?v=...",
  urlHelpText = `Supported platforms: ${supportedPlatforms.join(', ')}. Videos will be embedded and played directly from the platform.`,
  detectedText = "Detected:",

  titleRequiredText = "Title is required",
  fileTooLargeText = "File size exceeds maximum allowed size",
  invalidFileTypeText = "Please select a video file",
  invalidUrlText = "Invalid or unsupported video URL",

  // Visual props
  showTabs = true,
  showThumbnail = true,
  showProgress = true,
  showFileInfo = true,
  allowUrls = true,
  allowTags = true,
  requireTitle = true,
  dropzoneHeight = "16rem",
  ...props
}, ref) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadType, setUploadType] = useState<'file' | 'url'>('file')
  const [file, setFile] = useState<File | null>(null)
  const [videoUrl, setVideoUrl] = useState('')
  const [thumbnail, setThumbnail] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tags: '',
  })
  const [isDragging, setIsDragging] = useState(false)
  const [videoDuration, setVideoDuration] = useState<number | null>(null)
  const [urlValidation, setUrlValidation] = useState<{
    valid: boolean
    platform?: string
    error?: string
  } | null>(null)
  const [errors, setErrors] = useState<{
    title?: string
    file?: string
    url?: string
  }>({})

  const processFile = async (selectedFile: File) => {
    // Validate file size
    if (selectedFile.size > maxFileSize) {
      setErrors(prev => ({
        ...prev,
        file: `${fileTooLargeText} (${formatFileSize(selectedFile.size)} > ${formatFileSize(maxFileSize)})`
      }))
      return
    }

    // Validate file type
    const isValidType = acceptedTypes.some(type => {
      if (type === 'video/*') return selectedFile.type.startsWith('video/')
      return selectedFile.type === type
    })

    if (!isValidType) {
      setErrors(prev => ({ ...prev, file: invalidFileTypeText }))
      return
    }

    setErrors(prev => ({ ...prev, file: undefined }))
    setFile(selectedFile)

    // Get video duration
    if (showFileInfo) {
      try {
        const duration = await getVideoDuration(selectedFile)
        setVideoDuration(duration)
      } catch (error) {
        console.error('Failed to get video duration:', error)
      }
    }

    // Generate thumbnail
    if (showThumbnail) {
      try {
        const thumbnailUrl = await generateThumbnail(selectedFile)
        setThumbnail(thumbnailUrl)
      } catch (error) {
        console.error('Failed to generate thumbnail:', error)
        // Use object URL as fallback
        setThumbnail(URL.createObjectURL(selectedFile))
      }
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return
    await processFile(selectedFile)
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFile = e.dataTransfer.files?.[0]
    if (!droppedFile) return
    await processFile(droppedFile)
  }

  const handleUrlChange = (url: string) => {
    setVideoUrl(url)
    setErrors(prev => ({ ...prev, url: undefined }))

    if (url) {
      const validation = validateVideoUrl(url)
      setUrlValidation(validation)

      if (!validation.valid) {
        setErrors(prev => ({ ...prev, url: validation.error }))
      }

      // Clear thumbnail when URL changes
      if (thumbnail) {
        setThumbnail(null)
      }
    } else {
      setUrlValidation(null)
      setThumbnail(null)
    }
  }

  const validateForm = () => {
    const newErrors: typeof errors = {}

    if (requireTitle && !formData.title.trim()) {
      newErrors.title = titleRequiredText
    }

    if (uploadType === 'file' && !file) {
      newErrors.file = "Please select a video file"
    }

    if (uploadType === 'url' && (!videoUrl || !urlValidation?.valid)) {
      newErrors.url = urlValidation?.error || invalidUrlText
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    const uploadData: VideoUploadData = {
      title: formData.title,
      description: formData.description || undefined,
      tags: formData.tags || undefined,
      duration: videoDuration || undefined,
    }

    try {
      if (uploadType === 'file' && file) {
        await onFileUpload?.(file, uploadData)
      } else if (uploadType === 'url' && videoUrl) {
        await onUrlUpload?.(videoUrl, uploadData)
      }
      resetForm()
    } catch (error) {
      console.error('Upload failed:', error)
    }
  }

  const resetForm = () => {
    setFile(null)
    setVideoUrl('')
    setThumbnail(null)
    setFormData({ title: '', description: '', tags: '' })
    setVideoDuration(null)
    setUrlValidation(null)
    setErrors({})
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const getPlatformIcon = (platform?: string) => {
    switch (platform) {
      case 'youtube':
        return <Youtube className="h-4 w-4 text-red-500" />
      case 'vimeo':
        return <Video className="h-4 w-4 text-blue-500" />
      case 'dailymotion':
        return <Video className="h-4 w-4 text-orange-500" />
      default:
        return <Globe className="h-4 w-4 text-gray-500" />
    }
  }

  const renderFileUpload = () => (
    <div className="space-y-4">
      {!file ? (
        <div
          className="relative"
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedTypes.join(',')}
            onChange={handleFileSelect}
            className="hidden"
            id="video-upload"
            disabled={disabled || loading}
          />
          <label
            htmlFor="video-upload"
            className={cn(
              'flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-all duration-200',
              'hover:bg-gray-50 dark:hover:bg-gray-800',
              isDragging
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-950'
                : 'border-gray-300 dark:border-gray-600',
              (disabled || loading) && 'cursor-not-allowed opacity-50'
            )}
            style={{ height: dropzoneHeight }}
          >
            <div className="pointer-events-none flex flex-col items-center justify-center p-6 text-center">
              <Upload
                className={cn(
                  'mb-3 h-10 w-10',
                  isDragging ? 'text-blue-500' : 'text-gray-400'
                )}
              />
              <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="font-semibold">
                  {isDragging ? dropHereText : clickToUploadText}
                </span>
                {!isDragging && ` ${dragDropText}`}
              </p>
              <p className="text-xs text-gray-500">
                {fileTypesText} (MAX. {formatFileSize(maxFileSize)})
              </p>
            </div>
          </label>
        </div>
      ) : (
        <div className="rounded-lg border bg-white p-4 shadow-sm dark:bg-gray-900 dark:border-gray-700">
          <div className="flex items-start gap-4">
            {showThumbnail && thumbnail && (
              <div className="relative h-20 w-32 overflow-hidden rounded-md bg-gray-100 dark:bg-gray-800">
                <video
                  src={thumbnail}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <PlayCircle className="h-8 w-8 text-white" />
                </div>
              </div>
            )}
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 dark:text-gray-100">{file.name}</h4>
              {showFileInfo && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {formatFileSize(file.size)}
                  {videoDuration && ` • ${formatDuration(videoDuration)}`}
                </p>
              )}
              {showProgress && uploadProgress > 0 && uploadProgress < 100 && (
                <div className="mt-2">
                  <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                    <div
                      className="h-2 rounded-full bg-blue-500 transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
              {uploadProgress === 100 && (
                <div className="mt-2 flex items-center gap-1 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm">{completeText}</span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setFile(null)
                setThumbnail(null)
                setVideoDuration(null)
                setErrors(prev => ({ ...prev, file: undefined }))
              }}
              disabled={disabled || loading}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {errors.file && (
        <p className="text-sm text-red-600 dark:text-red-400">{errors.file}</p>
      )}
    </div>
  )

  const renderUrlUpload = () => (
    <div className="space-y-4">
      <div>
        <label htmlFor="video-url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {urlLabel}
        </label>
        <div className="relative">
          <input
            id="video-url"
            type="url"
            value={videoUrl}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder={urlPlaceholder}
            disabled={disabled || loading}
            className={cn(
              'w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              'dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              errors.url ? 'border-red-500' : 'border-gray-300'
            )}
          />
          {urlValidation?.platform && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {getPlatformIcon(urlValidation.platform)}
            </div>
          )}
        </div>
        {errors.url ? (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.url}</p>
        ) : urlValidation?.valid && urlValidation.platform && (
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {detectedText} {urlValidation.platform.charAt(0).toUpperCase() + urlValidation.platform.slice(1)} video
          </p>
        )}
      </div>

      {urlHelpText && (
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md dark:bg-blue-950 dark:border-blue-800">
          <AlertCircle className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700 dark:text-blue-300">{urlHelpText}</p>
        </div>
      )}
    </div>
  )

  const renderForm = () => (
    <div className="space-y-4">
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {titleLabel} {requireTitle && <span className="text-red-500">*</span>}
        </label>
        <input
          id="title"
          value={formData.title}
          onChange={(e) => {
            setFormData({ ...formData, title: e.target.value })
            setErrors(prev => ({ ...prev, title: undefined }))
          }}
          placeholder={titlePlaceholder}
          disabled={disabled || loading}
          className={cn(
            'w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent',
            'dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            errors.title ? 'border-red-500' : 'border-gray-300'
          )}
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.title}</p>
        )}
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {descriptionLabel}
        </label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder={descriptionPlaceholder}
          rows={3}
          disabled={disabled || loading}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>

      {allowTags && (
        <div>
          <label htmlFor="tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {tagsLabel}
          </label>
          <input
            id="tags"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
            placeholder={tagsPlaceholder}
            disabled={disabled || loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {tagsHelpText && (
            <p className="mt-1 text-xs text-gray-500">{tagsHelpText}</p>
          )}
        </div>
      )}
    </div>
  )

  const renderTabs = () => (
    <div className="mb-6">
      <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 p-1 bg-gray-100 dark:bg-gray-800">
        <button
          type="button"
          onClick={() => setUploadType('file')}
          disabled={disabled || loading}
          className={cn(
            'flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors',
            uploadType === 'file'
              ? 'bg-white text-gray-900 shadow dark:bg-gray-900 dark:text-gray-100'
              : 'text-gray-600 dark:text-gray-400',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          <Upload className="h-4 w-4" />
          {uploadTabText}
        </button>
        {allowUrls && (
          <button
            type="button"
            onClick={() => setUploadType('url')}
            disabled={disabled || loading}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors',
              uploadType === 'url'
                ? 'bg-white text-gray-900 shadow dark:bg-gray-900 dark:text-gray-100'
                : 'text-gray-600 dark:text-gray-400',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <Link className="h-4 w-4" />
            {urlTabText}
          </button>
        )}
      </div>
    </div>
  )

  return (
    <div ref={ref} className={cn('max-w-2xl mx-auto', className)} {...props}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {showTabs && allowUrls && renderTabs()}

        {uploadType === 'file' ? renderFileUpload() : renderUrlUpload()}

        {renderForm()}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={disabled || loading}
            className={cn(
              'flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors',
              'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {uploadingText}
              </>
            ) : (
              uploadButtonText
            )}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
                'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {cancelButtonText}
            </button>
          )}
        </div>
      </form>
    </div>
  )
})

VideoUpload.displayName = "VideoUpload"